/* eslint-disable consistent-return */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { pusher } from '#services/PusherService';

import { LEGAL_CASES_QUERY_KEY } from './queries/useLegalCases';

const SOURCE_TO_ROUTE = {
  pjn: 'eje',
  eje: 'eje',
  mev: 'mev'
};

const TOAST_DURATION = 10_000;

export const useLegalCaseNotifications = () => {
  const queryClient = useQueryClient();
  const currentUser = useSelector(state => state.auth.currentUser);
  const history = useHistory();

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }

    const userId = currentUser.id;
    const channel = pusher.subscribe(`user-${userId}`);

    const handleImportCompleted = async data => {
      const { legalCaseId, source, caseNumber } = data;
      const routeType = SOURCE_TO_ROUTE[source] || 'manual';

      await queryClient.invalidateQueries([LEGAL_CASES_QUERY_KEY, 'importStatus', legalCaseId]);

      toast.success(`Importación completada: ${caseNumber || 'Expediente'}`, {
        description: 'El expediente fue importado correctamente',
        action: {
          label: 'Ver expediente',
          onClick: () => history.push(`/expedientes/${routeType}/${legalCaseId}`)
        },
        duration: TOAST_DURATION
      });
    };

    const handleImportFailed = async data => {
      const { legalCaseId, caseNumber, error } = data;

      await queryClient.invalidateQueries([LEGAL_CASES_QUERY_KEY, 'importStatus', legalCaseId]);

      toast.error(`Importación fallida: ${caseNumber || 'Expediente'}`, {
        description: error || 'Ocurrió un error durante la importacion del expediente',
        duration: TOAST_DURATION
      });
    };

    const handleUpdateCompleted = async data => {
      const { legalCaseId, source, caseNumber } = data;
      const routeType = SOURCE_TO_ROUTE[source] || 'manual';

      await queryClient.invalidateQueries([LEGAL_CASES_QUERY_KEY, 'importStatus', legalCaseId]);

      toast.success(`Sincronización completada: ${caseNumber || 'Expediente'}`, {
        description: 'El expediente fue sincronizado correctamente',
        action: {
          label: 'Ver expediente',
          onClick: () => history.push(`/expedientes/${routeType}/${legalCaseId}`)
        },
        duration: TOAST_DURATION
      });
    };

    const handleUpdateFailed = async data => {
      const { legalCaseId, caseNumber, error } = data;

      await queryClient.invalidateQueries([LEGAL_CASES_QUERY_KEY, 'importStatus', legalCaseId]);

      toast.error(`Sincronización fallida: ${caseNumber || 'Expediente'}`, {
        description: error || 'Ocurrió un error durante la sincronización del expediente',
        duration: TOAST_DURATION
      });
    };

    channel.bind('import-job-completed', handleImportCompleted);
    channel.bind('import-job-failed', handleImportFailed);
    channel.bind('update-job-completed', handleUpdateCompleted);
    channel.bind('update-job-failed', handleUpdateFailed);

    return () => {
      channel.unbind('import-job-completed', handleImportCompleted);
      channel.unbind('import-job-failed', handleImportFailed);
      channel.unbind('update-job-completed', handleUpdateCompleted);
      channel.unbind('update-job-failed', handleUpdateFailed);
      pusher.unsubscribe(`user-${userId}`);
    };
  }, [currentUser?.id, history]);
};
